/**
 * The PBS Live / Copilot widget is injected into the DOM by an external script
 * (initialize-pbs-widget.js) and mounts its own React app into `#root-pbslive`,
 * outside of our React tree and as a sibling of our app under <body>.
 *
 * Because it lives outside every Radix modal subtree, an open Radix Dialog/Sheet
 * makes it (a) unclickable — Radix sets `pointer-events: none` on <body> — and
 * (b) untypeable — Radix's focus trap steals focus back into the modal. These
 * helpers let the modal primitives and app shell treat the widget as an
 * always-interactive surface. See usePbsLiveModalInterop() and dialog/sheet.
 */
export const PBS_LIVE_WIDGET_ID = "root-pbslive";
export const PBS_LIVE_WIDGET_SELECTOR = `#${PBS_LIVE_WIDGET_ID}`;

/** True when an event target is inside the PBS Live widget subtree. */
export function isInsidePbsLiveWidget(target: EventTarget | null | undefined): boolean {
  return target instanceof Element && !!target.closest(PBS_LIVE_WIDGET_SELECTOR);
}

/**
 * Radix outside-interaction events (onInteractOutside/onPointerDownOutside/
 * onFocusOutside) are CustomEvents dispatched on the modal content node, so the
 * real target lives on `detail.originalEvent.target`. Returns true when the
 * originating interaction happened inside the PBS Live widget, so the modal
 * should not treat it as an outside dismiss.
 */
export function isPbsLiveOutsideInteraction(event: {
  detail?: { originalEvent?: Event };
}): boolean {
  return isInsidePbsLiveWidget(event.detail?.originalEvent?.target);
}
